/**
 * An assistant's answer, read as the little Markdown it is told to write.
 *
 * The assistant writes paragraphs, "- " and "1. " lists, **bold** and links
 * to the app's own screens (`optivo-service/src/services/assistant/guide.ts`),
 * and nothing else is drawn: there is no Markdown library in the UI, and an
 * answer is the model's own text, so it is never treated as HTML. This turns
 * the text into blocks the window renders as React elements; whatever is not
 * part of that subset stays as the text it was.
 *
 * It is total: any string at all reads as something, and it never throws.
 * The answer is read again with every piece that streams in, so a mark whose
 * end has not arrived yet (`**2 3`, `[Ceny](/pri`) reads as plain text until
 * it does. A heading reads as a bold line, and a code fence or a rule is
 * dropped — the guide asks for neither, and their marks would only be noise.
 * Whether a link may be followed is decided where it is drawn
 * (`assistant-links.ts`), never here.
 */

export type AnswerInline =
    | { kind: "text"; text: string }
    | { kind: "strong"; children: AnswerInline[] }
    | { kind: "code"; text: string }
    | { kind: "link"; target: string; label: string };

/** One line of a paragraph or a list item. */
export type AnswerLine = AnswerInline[];

export type AnswerBlock =
    | { kind: "paragraph"; lines: AnswerLine[] }
    | { kind: "list"; ordered: boolean; start: number; items: AnswerLine[][] };

const BULLET = /^\s*[-*•+]\s+(.*)$/;
const NUMBERED = /^\s*(\d{1,3})[.)]\s+(.*)$/;
/** Only a heading's opening is matched by a pattern; see `headingText`. */
const HEADING_PREFIX = /^\s{0,3}#{1,6}\s+/;
const FENCE = /^\s*(```|~~~)/;
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const QUOTE = /^\s*>\s?(.*)$/;
const INDENTED = /^\s+\S/;
const LINK = /^\[([^\]\n]+)\]\(([^()\s]+)\)/;

/**
 * A heading's text, its closing marks taken off; null for a line that is not
 * one. The closing marks are cut by hand rather than by a pattern: one that
 * matched a heading whole tried every way of splitting a run of spaces
 * between the text and the marks, which took seconds over a few thousand.
 */
function headingText(raw: string): string | null {
    const prefix = HEADING_PREFIX.exec(raw);
    if (!prefix) return null;
    const text = raw.slice(prefix[0].length).trimEnd();
    let end = text.length;
    while (end > 0 && text[end - 1] === "#") end -= 1;
    return text.slice(0, end).trimEnd();
}

function pushText(into: AnswerInline[], value: string): void {
    if (value === "") return;
    const last = into[into.length - 1];
    if (last && last.kind === "text") last.text += value;
    else into.push({ kind: "text", text: value });
}

/** The inline marks of one line: bold (not nested in bold), code and links. */
function parseInline(line: string, allowStrong = true): AnswerLine {
    const out: AnswerInline[] = [];
    let i = 0;
    let plain = "";
    const flush = () => {
        pushText(out, plain);
        plain = "";
    };
    while (i < line.length) {
        if (allowStrong && line.startsWith("**", i)) {
            const end = line.indexOf("**", i + 2);
            const inner = end === -1 ? "" : line.slice(i + 2, end);
            if (inner.trim() !== "") {
                flush();
                out.push({ kind: "strong", children: parseInline(inner, false) });
                i = end + 2;
                continue;
            }
            plain += "**";
            i += 2;
            continue;
        }
        const char = line[i];
        if (char === "`") {
            const end = line.indexOf("`", i + 1);
            if (end > i + 1) {
                flush();
                out.push({ kind: "code", text: line.slice(i + 1, end) });
                i = end + 1;
                continue;
            }
        } else if (char === "[") {
            const link = LINK.exec(line.slice(i));
            if (link) {
                flush();
                out.push({ kind: "link", target: link[2], label: link[1] });
                i += link[0].length;
                continue;
            }
        }
        plain += char;
        i += 1;
    }
    flush();
    return out;
}

function readBlocks(text: string): AnswerBlock[] {
    const blocks: AnswerBlock[] = [];
    let paragraph: AnswerLine[] | null = null;
    let list: Extract<AnswerBlock, { kind: "list" }> | null = null;
    const close = () => {
        paragraph = null;
        list = null;
    };
    const addParagraphLine = (content: string) => {
        if (list) close();
        if (!paragraph) {
            paragraph = [];
            blocks.push({ kind: "paragraph", lines: paragraph });
        }
        paragraph.push(parseInline(content));
    };
    const addItem = (ordered: boolean, start: number, content: string) => {
        if (paragraph) close();
        if (!list || list.ordered !== ordered) {
            list = { kind: "list", ordered, start, items: [] };
            blocks.push(list);
        }
        list.items.push([parseInline(content)]);
    };

    for (const raw of text.replace(/\r\n?/g, "\n").split("\n")) {
        if (raw.trim() === "") {
            close();
            continue;
        }
        if (FENCE.test(raw)) continue;
        if (RULE.test(raw)) {
            close();
            continue;
        }
        const heading = headingText(raw);
        if (heading !== null) {
            close();
            blocks.push({ kind: "paragraph", lines: [[{ kind: "strong", children: parseInline(heading, false) }]] });
            continue;
        }
        const bullet = BULLET.exec(raw);
        if (bullet) {
            addItem(false, 1, bullet[1]);
            continue;
        }
        const numbered = NUMBERED.exec(raw);
        if (numbered) {
            addItem(true, Number(numbered[1]), numbered[2]);
            continue;
        }
        const current = list as Extract<AnswerBlock, { kind: "list" }> | null;
        if (current && INDENTED.test(raw)) {
            current.items[current.items.length - 1].push(parseInline(raw.trim()));
            continue;
        }
        const quote = QUOTE.exec(raw);
        addParagraphLine(quote ? quote[1] : raw.trim());
    }
    return blocks;
}

/** The answer as blocks to draw; never throws. */
export function parseAssistantAnswer(text: string): AnswerBlock[] {
    try {
        return readBlocks(typeof text === "string" ? text : "");
    } catch {
        // The last guard of a total reader: the answer as its plain lines.
        return [{ kind: "paragraph", lines: String(text).split("\n").map((line) => [{ kind: "text", text: line }]) }];
    }
}

function inlineText(nodes: AnswerInline[]): string {
    return nodes.map((node) => {
        switch (node.kind) {
            case "text":
            case "code":
                return node.text;
            case "strong":
                return inlineText(node.children);
            case "link":
                return node.label;
        }
    }).join("");
}

/** The answer's words without its marks, links read as their labels: what a screen reader is told. */
export function answerPlainText(text: string): string {
    return parseAssistantAnswer(text)
        .flatMap((block) => (block.kind === "paragraph" ? block.lines : block.items.flat()))
        .map((line) => inlineText(line).trim())
        .filter((line) => line !== "")
        .join(" ");
}
